// Selection visualization: white cell-boundary lines around every selected
// voxel — the selection reads as its voxels' gridlines turning white, without
// recoloring the voxel faces. depthFunc = LessEqualDepth lets the lines sit on
// the coplanar voxel-face boundaries without z-fighting; occluded (interior)
// edges fail the depth test, so only the visible surface lines show. The lines
// rebuild whenever the Selection or VoxelGrid version bumps.
//
// Zoom-aware opacity: a 1px-wide white edge is a thin border when a voxel spans
// many screen pixels, but swamps the colored face once the voxel shrinks to a
// few pixels — zoomed-out selections would otherwise read as solid white. So we
// fade the outline toward transparent as a voxel's on-screen size drops, which
// lets the voxel colors show through (matching how MagicaVoxel keeps the model
// readable while highlighted). This tracks the camera, so it re-evaluates every
// frame, not just on selection/grid version bumps.

import * as THREE from "three";
import { CELL_SIZE, VoxelGrid } from "../model/voxel-grid";
import type { Selection } from "../model/selection";

const SELECTION_COLOR = 0xffffff; // white gridlines

// Opacity ramp keyed on a voxel's on-screen edge length (CSS px). At/above
// PX_FULL the crisp white look is preserved; at/below PX_MIN it fades to a faint
// tint so the underlying voxel colors dominate.
const PX_FULL = 16;
const PX_MIN = 4;
const OPACITY_FULL = 1.0;
const OPACITY_MIN = 0.12;

const _tmpWorld = new THREE.Vector3();
const _tmpWorldEdge = new THREE.Vector3();
const _tmpRight = new THREE.Vector3();

interface LineBucket {
  pos: number[];
}

export class SelectionOutline {
  private _group = new THREE.Group();
  private edgeMesh: THREE.LineSegments;
  private edgeMat: THREE.LineBasicMaterial;
  private lastSelectionVersion = -1;
  private lastGridVersion = -1;
  // Centroid (grid-local) of the live selection cells, plus a count, kept in
  // sync by rebuild(). The opacity pass projects this single point to estimate a
  // voxel's on-screen size rather than measuring per cell.
  private _centroidLocal = new THREE.Vector3();
  private _cellCount = 0;

  constructor() {
    // transparent (opacity 1, still fully opaque-looking) so it renders in the
    // SAME pass as the mesher's grid lines — they're transparent, so an opaque
    // material would draw before them and get painted over. renderOrder 13 then
    // puts these on top of the grid lines (10) + edge lines (11).
    const edgeMat = new THREE.LineBasicMaterial({
      color: SELECTION_COLOR,
      transparent: true,
      opacity: 1.0,
    });
    edgeMat.depthFunc = THREE.LessEqualDepth;
    this.edgeMat = edgeMat;
    this.edgeMesh = new THREE.LineSegments(new THREE.BufferGeometry(), edgeMat);
    this.edgeMesh.renderOrder = 13;
    this._group.add(this.edgeMesh);
  }

  get object3D(): THREE.Object3D {
    return this._group;
  }

  // Rebuilds the line geometry on selection/grid version bumps, then re-evaluates
  // the zoom-aware opacity every frame (the camera may have moved without any
  // version change). Pass the camera being rendered and the canvas width in CSS
  // pixels so the projected voxel size matches what the user sees.
  sync(
    selection: Selection,
    grid: VoxelGrid,
    camera: THREE.Camera,
    viewportWidthPx: number,
  ): void {
    if (
      selection.version !== this.lastSelectionVersion ||
      grid.version !== this.lastGridVersion
    ) {
      this.lastSelectionVersion = selection.version;
      this.lastGridVersion = grid.version;
      this.rebuild(selection, grid);
    }
    this.updateOpacity(camera, viewportWidthPx);
  }

  private rebuild(selection: Selection, grid: VoxelGrid): void {
    const edges: LineBucket = { pos: [] };
    // Dedup edges across the whole selection (in integer cell-corner space, so
    // no float-key fuzz): an edge shared by two emitted faces — or by two
    // adjacent selected cells — is pushed once, not twice.
    const seen = new Set<number>();
    let cx = 0;
    let cy = 0;
    let cz = 0;
    let count = 0;
    for (const c of selection.entries()) {
      // Skip cells whose voxel no longer exists (e.g. erased out from under the
      // selection) — their highlighted edges should vanish with them.
      if (!grid.has(c.ix, c.iy, c.iz)) continue;
      emitSelectedCellEdges(grid, c.ix, c.iy, c.iz, edges, seen);
      cx += c.ix + 0.5;
      cy += c.iy + 0.5;
      cz += c.iz + 0.5;
      count++;
    }
    this._cellCount = count;
    if (count > 0) {
      this._centroidLocal.set(
        (cx / count) * CELL_SIZE,
        (cy / count) * CELL_SIZE,
        (cz / count) * CELL_SIZE,
      );
    }
    this.edgeMesh.geometry.dispose();
    this.edgeMesh.geometry = new THREE.BufferGeometry().setAttribute(
      "position",
      new THREE.Float32BufferAttribute(edges.pos, 3),
    );
  }

  // Estimate a voxel's on-screen edge length by projecting the selection
  // centroid and a point one cell to its right, then ramp opacity over that.
  private updateOpacity(camera: THREE.Camera, viewportWidthPx: number): void {
    if (this._cellCount === 0 || viewportWidthPx <= 0) return;
    const world = _tmpWorld.copy(this._centroidLocal);
    this._group.localToWorld(world);
    // Camera's world-space right axis (column 0 of its world matrix); offsetting
    // along it keeps the measured delta horizontal on screen.
    _tmpRight.setFromMatrixColumn(camera.matrixWorld, 0).normalize();
    const worldEdge = _tmpWorldEdge.copy(world).addScaledVector(_tmpRight, CELL_SIZE);
    world.project(camera);
    worldEdge.project(camera);
    // NDC x spans [-1, 1] across the full width, hence the 0.5 factor.
    const pixels = Math.abs(worldEdge.x - world.x) * 0.5 * viewportWidthPx;
    const t = clamp01((pixels - PX_MIN) / (PX_FULL - PX_MIN));
    this.edgeMat.opacity = OPACITY_MIN + t * (OPACITY_FULL - OPACITY_MIN);
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// The 6 faces of a unit cell, each as {neighbor cell offset, 4 corner offsets}.
// A face contributes its 4 boundary edges only when its neighbor cell is empty
// — i.e. the face is on the model's visible surface. Buried faces (neighbor
// solid) emit nothing: their edges fail the depth test against the surrounding
// solid anyway, so this drops geometry without changing what's drawn. An
// isolated selected voxel has all 6 faces exposed, so its full 12-edge cube is
// still emitted (each edge deduped across the two faces that share it).
const FACES: ReadonlyArray<{
  n: readonly [number, number, number];
  c: ReadonlyArray<readonly [number, number, number]>;
}> = [
  { n: [-1, 0, 0], c: [[0, 0, 0], [0, 1, 0], [0, 1, 1], [0, 0, 1]] },
  { n: [1, 0, 0], c: [[1, 0, 0], [1, 1, 0], [1, 1, 1], [1, 0, 1]] },
  { n: [0, -1, 0], c: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]] },
  { n: [0, 1, 0], c: [[0, 1, 0], [1, 1, 0], [1, 1, 1], [0, 1, 1]] },
  { n: [0, 0, -1], c: [[0, 0, 0], [1, 0, 0], [1, 1, 0], [0, 1, 0]] },
  { n: [0, 0, 1], c: [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]] },
];

// Pack a cell-corner (ix..ix+128 per axis, plus 1 for the far corner) into a
// single int; 257 leaves headroom past the 128-cell volume cap.
const CK = 257;
function cornerKey(x: number, y: number, z: number): number {
  return (x * CK + y) * CK + z;
}

function emitSelectedCellEdges(
  grid: VoxelGrid,
  ix: number,
  iy: number,
  iz: number,
  out: LineBucket,
  seen: Set<number>,
): void {
  for (const f of FACES) {
    if (grid.has(ix + f.n[0], iy + f.n[1], iz + f.n[2])) continue; // buried face
    for (let i = 0; i < 4; i++) {
      const a = f.c[i];
      const b = f.c[(i + 1) % 4];
      const ax = ix + a[0], ay = iy + a[1], az = iz + a[2];
      const bx = ix + b[0], by = iy + b[1], bz = iz + b[2];
      const ka = cornerKey(ax, ay, az);
      const kb = cornerKey(bx, by, bz);
      // Order-independent edge key so a→b and b→a dedup to one segment.
      const key = ka < kb ? ka * CK * CK * CK + kb : kb * CK * CK * CK + ka;
      if (seen.has(key)) continue;
      seen.add(key);
      out.pos.push(
        ax * CELL_SIZE, ay * CELL_SIZE, az * CELL_SIZE,
        bx * CELL_SIZE, by * CELL_SIZE, bz * CELL_SIZE,
      );
    }
  }
}
